import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgChartsModule } from 'ng2-charts';
import { ChartData, ChartOptions } from 'chart.js';

@Component({
  selector: 'app-reuseable-bar-chart',
  standalone: true,
  imports: [CommonModule, NgChartsModule, FormsModule],
  templateUrl: './bar-chart.component.html',
  styleUrl: './bar-chart.component.css'
})
export class BarChartComponent implements OnChanges, OnInit {
  @Input() title: string = '';
  @Input() alttitle: string = '';
  @Input() labels: any[] = [];
  @Input() data: any[] = [];
  @Input() data2: any[] = [];
  @Input() currentPage: number = 1;
  @Input() totalPages: number = 1;
  // Backing field + setter/getter to react to page size changes even when changed locally via ngModel
  private _itemsPerPage: number = 10;
  @Input() set ItemsPerPage(value: number) {
    const v = Number(value);
    this._itemsPerPage = Math.max(1, isNaN(v) ? 10 : v);
    // Ensure the chart re-renders when items per page changes from the dropdown
    this.updateChart();
  }
  get ItemsPerPage(): number {
    return this._itemsPerPage;
  }
  @Input() colors: string[] = [];
  @Input() altcolors: string[] = [];
  @Input() goToPage!: (page: number) => void;
  @Output() pageChange = new EventEmitter<number>();
  @Input() parentFilterChange: ((key: string) => void) | undefined;
  @Input() filters: { label: string; key: string }[] = [];
  @Input() selectedFilter: string = 'all';
  @Input() showFilters: boolean = false;
  @Input() totalItemsCount: number = 0;
  @Input() pageSizeChange: ((size: number) => void) | undefined;
  @Input() isMultiSet: boolean = false
  @Input() ChartOptions: any;
  @Input() hasCustom: boolean = false;
  @Input() horizontal: boolean = false;
  @Output() filterChange = new EventEmitter<string>();

  chartData!: ChartData<'bar'>;
  chartOptions!: ChartOptions<'bar'>;

  // Derived display values
  get pageStartDisplay(): number {
    if (!this.totalItemsCount || this.totalItemsCount <= 0) return 0;
    return (this.currentPage - 1) * this.ItemsPerPage + 1;
  }

  get pageEndDisplay(): number {
    if (!this.totalItemsCount || this.totalItemsCount <= 0) return 0;
    return Math.min(this.currentPage * this.ItemsPerPage, this.totalItemsCount);
  }

  ngOnInit(): void {
    // If no filter is selected or if selectedFilter is empty, default to 'all'
    if (!this.selectedFilter && this.filters.length > 0) {
      // Find the 'all' filter or use the first filter
      const allFilter = this.filters.find(f => f.key === 'all') || this.filters[0];
      if (allFilter) {
        this.selectedFilter = allFilter.key;
      }
    }
  }

  onFilterChange(key: string): void {
    // Update the selected filter locally
    this.selectedFilter = key;

    // Call parent handler if provided
    if (this.parentFilterChange) {
      this.parentFilterChange(key);
    }

    // Emit the event to notify the parent component
    this.filterChange.emit(key);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.updateChart();

    if (this.hasCustom == false) {

      this.chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        ...(this.horizontal ? { indexAxis: 'y' as const } : {}),
        layout: this.horizontal ? { padding: { right: 48 } } : {},
        scales: {
          x: {
            stacked: true,
            grid: { display: this.horizontal },
            ticks: this.horizontal ? {
              callback: (value) => this.formatZAR(Number(value))
            } : {},
          },
          y: {
            stacked: true,
            title: { display: false, text: '' },
            grid: { color: this.horizontal ? 'transparent' : '#e5e7eb' },
            ticks: this.horizontal ? { font: { size: 11 } } : {
              callback: (value) => this.formatZAR(Number(value))
            }
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#1f2937',
            bodyColor: '#f9fafb',
            titleColor: '#f9fafb',
            borderWidth: 0,
            callbacks: {
              label: (context: any) => {
                const v = this.horizontal
                  ? (context?.parsed?.x ?? context?.raw ?? 0) as number
                  : (context?.parsed?.y ?? context?.raw ?? 0) as number;
                const label = context?.dataset?.label ?? 'Value';
                return `${label}: ${this.formatZAR(Number(v))}`;
              }
            }
          },
        },
      };
    }
    else {
      this.chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            titleColor: '#333',
            bodyColor: '#333',
            borderColor: 'rgba(0, 0, 0, 0.1)',
            borderWidth: 1,
            cornerRadius: 4,
            bodyFont: { size: 13 },
            titleFont: { size: 14, weight: 'bold' },
            displayColors: true,
            boxPadding: 5,
            callbacks: {
              label: (context: any) => {
                const v = (context?.parsed?.y ?? context?.raw ?? 0) as number;
                const label = context?.dataset?.label ?? 'Value';
                return `${label}: ${this.formatZAR(Number(v))}`;
              }
            }
          },
        },
        scales: {
          x: {
            grid: {
              display: false
            },
            ticks: {
              color: '#555',
              font: { size: 11 }
            }
          },
          y: {
            beginAtZero: true,
            grid: {
              color: 'rgba(0, 0, 0, 0.05)'
            },
            ticks: {
              color: '#555',
              font: { size: 11 },
              callback: (value) => this.formatZAR(Number(value))
            }
          },
        },
      };
    }

  }

  private formatZAR(value: number): string {
    try {
      return new Intl.NumberFormat('en-ZA', {
        style: 'currency',
        currency: 'ZAR',
        maximumFractionDigits: 2,
      }).format(Number(value) || 0);
    } catch {
      const num = Number(value) || 0;
      return `R ${num.toLocaleString('en-ZA', { maximumFractionDigits: 2 })}`;
    }
  }

  private updateChart(): void {
    if (!this.labels || !this.data) {
      //console.warn('Labels or data is not available');
      return;
    }

    // Normalize lengths and then slice according to pagination
    const dataLength = Math.min(this.labels.length, this.data.length);
    const allLabels = this.labels.slice(0, dataLength);
    const allData = this.data.slice(0, dataLength);

    const itemsPerPage = Math.max(1, this.ItemsPerPage || 1);
    const currentPage = Math.max(1, this.currentPage || 1);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    const labels = allLabels.slice(startIndex, endIndex);
    const data = allData.slice(startIndex, endIndex);
    // Create colors array matching the data length
    const chartColors: string[] = [];
    for (let i = 0; i < data.length; i++) {
      const color = (this.colors && this.colors.length > 0)
        ? this.colors[i % this.colors.length]
        : '#0ea5e9';
      chartColors.push(color);
    }

    // Prepare the dataset
    const dataset: any = {
      label: this.title || 'Data',
      data: data,
      backgroundColor: chartColors,
      borderRadius: 4,
      borderWidth: 0,
      borderColor: 'transparent',
    };
    // Only fix bar thickness for vertical bars; horizontal bars auto-size based on container height
    if (!this.horizontal) {
      dataset.barThickness = 40;
      dataset.maxBarThickness = 50;
    }

    // Update the chart data
    this.chartData = {
      labels: labels,
      datasets: [dataset]
    };
  }
}
